import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StageBadge from '../components/StageBadge';
import ContractModal from '../components/ContractModal';
import MarketDealModal from '../components/MarketDealModal';
import GmailComposeModal from '../components/GmailComposeModal';
import DealEmailPanel from '../components/DealEmailPanel';
import NegotiationModal from '../components/NegotiationModal';
import DealDocuments from '../components/DealDocuments';
import { proxyPhoto } from '../utils/photo';
import {
  fmtMoney, fmtDate, InlineMoney, FollowUpBadge, ListingStatusCell, NotesThread,
} from './Deals';

// ── The full deal page (/deals/:id) — the deal's home. Links out to /underwriting/:id for the
// comp/ARV analysis engine. Pass 1: everything except the Property Packet report (next pass).

const money = (n) => (n == null || n === '' || isNaN(Number(n))) ? '' : '$' + Number(n).toLocaleString();
const num = (n) => (n == null || n === '' || isNaN(Number(n))) ? '' : Number(n).toLocaleString();

// Fields the text pitch needs; blanks are surfaced in the "what's missing" gate before generating.
const PITCH_REQUIRED = [
  ['address', 'Address'], ['beds', 'Beds'], ['baths', 'Baths'],
  ['garage', 'Garage count'], ['sqft', 'Sqft'], ['lot_size', 'Lot size'], ['year_built', 'Year built'],
  ['marketing_price', 'Price'], ['earnest_money', 'EMD'], ['title_company', 'Title company'],
  ['photos_link', 'Photos link'], ['pitch_details', 'Pitch details (the "Condition:" body)'],
  ['access_notes', 'Access notes'],
];

function pitchMissing(deal, selectedCount) {
  const m = [];
  for (const [k, label] of PITCH_REQUIRED) {
    // The pitch prints marketing_price (our sell price) falling back to asking_price, so the gate has
    // to accept the same fallback — otherwise a Google-Sheet row that has always carried its price in
    // asking_price would report a missing Price it actually has.
    const v = k === 'marketing_price' ? (deal.marketing_price ?? deal.asking_price) : deal[k];
    if (v == null || String(v).trim() === '') m.push(label);
  }
  if (!(deal.close_of_escrow || deal.close_date)) m.push('COE (close date)');
  if (!selectedCount) m.push('At least one comp selected');
  return m;
}

// Deterministic text-pitch builder — mirrors the client's exact format. Pool line only when pool=1.
function buildPitch(deal, comps) {
  const bbg = [deal.beds, deal.baths, deal.garage].map(x => (x == null || x === '' ? '?' : x)).join('/');
  const poolTag = deal.pool ? ' + Pool' : '';
  const strat = deal.strategy ? `  ${deal.strategy}` : '';
  const cityline = [deal.city, deal.state].filter(Boolean).join(', ');
  const line1 = `${deal.address || ''}${cityline ? ', ' + cityline : ''}${deal.zip ? ' ' + deal.zip : ''}`;
  const line2 = `${deal.neighborhood || ''} (${bbg}${poolTag})${strat}`.trim();
  const coe = deal.close_of_escrow || (deal.close_date ? fmtDate(deal.close_date) : '');
  const lotRaw = deal.lot_size != null ? String(deal.lot_size).trim() : '';
  const lotNum = Number(lotRaw.replace(/[, ]/g, ''));
  const lot = !lotRaw ? '' : (isNaN(lotNum) ? lotRaw : `${lotNum.toLocaleString()} sq ft`);

  const L = [];
  L.push(line1);
  L.push(line2);
  L.push('');
  // Our sell price (migration 076), falling back to asking_price for rows that predate it and for
  // Google-Sheet rows, whose Price column has always been the sell price.
  L.push(`Price: ${money(deal.marketing_price ?? deal.asking_price)}`);
  L.push(`EMD: ${money(deal.earnest_money)}${deal.title_company ? ' - ' + deal.title_company : ''}`);
  L.push(`COE: ${coe}`);
  L.push('');
  L.push(`${deal.year_built || ''} Year Build`);
  L.push(`${deal.beds || ''} Bed, ${deal.baths || ''} Bath, ${deal.garage || ''} Car Garage`);
  L.push(`${num(deal.sqft)} sq ft${lot ? ', ' + lot + ' lot' : ''}`);
  if (deal.pool) L.push('Private Pool');
  L.push('');
  L.push(`Pics: ${deal.photos_link || ''}`);
  L.push('');
  L.push(`Condition: ${deal.pitch_details || ''}`);
  L.push('');
  L.push('Comps:');
  for (const c of comps) {
    const price = c.sold_price ?? c.price;
    const d = c.close_date ? fmtDate(c.close_date) : '';
    L.push(`- ${c.address || ''} - ${c.beds ?? '?'} Bed, ${c.baths ?? '?'} Bath, ${num(c.sqft)} sq ft - Sold for ${money(price)} on ${d}`);
  }
  L.push('');
  // HOA line: omit entirely when the deal is flagged "not in an HOA" (hoa === 0).
  if (deal.hoa !== 0) {
    const hoa = [];
    if (deal.hoa_monthly) hoa.push(`${money(deal.hoa_monthly)}/m`);
    if (deal.hoa_prepaid) hoa.push(`${money(deal.hoa_prepaid)} prepaid assoc`);
    if (deal.hoa_disclosure) hoa.push(`${money(deal.hoa_disclosure)} Disclosure Fee`);
    if (hoa.length) L.push(`HOA: ${hoa.join(', ')}`);
    else if (deal.hoa === 1) L.push('HOA: Yes');
  }
  L.push(`Access: ${deal.access_notes || ''}`);
  return L.join('\n');
}

// ── styles ────────────────────────────────────────────────────────────────────
const card = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: '18px 20px' };
const h2 = { fontSize: 14, fontWeight: 700, color: 'var(--white)', margin: 0 };
const lbl = { fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--gray-500)', marginBottom: 5 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, fontFamily: 'inherit', outline: 'none' };
const btn = { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-100)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const btnPrimary = { ...btn, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff' };
const btnGold = { ...btn, background: 'var(--gold)', border: '1px solid var(--gold)', color: '#3a2c05' };

// A labeled field that saves on blur (text/number/textarea/select).
function TermField({ label, value, onSave, type = 'text', options, full, rows }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const commit = () => { const nv = v === '' ? null : v; if (String(nv ?? '') !== String(value ?? '')) onSave(nv); };
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <div style={lbl}>{label}</div>
      {options ? (
        <select value={v} onChange={e => { setV(e.target.value); const nv = e.target.value === '' ? null : e.target.value; if (String(nv ?? '') !== String(value ?? '')) onSave(nv); }} style={inputStyle}>
          {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : rows ? (
        <textarea value={v} rows={rows} onChange={e => setV(e.target.value)} onBlur={commit} style={{ ...inputStyle, resize: 'vertical' }} />
      ) : (
        <input type={type} value={v} onChange={e => setV(e.target.value)} onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter' && type !== 'textarea') e.currentTarget.blur(); }} style={inputStyle} />
      )}
    </div>
  );
}

function Metric({ label, children }) {
  return <div><div style={lbl}>{label}</div><div>{children}</div></div>;
}

export default function DealPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comps, setComps] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [pitch, setPitch] = useState(null);      // generated pitch text (null = not generated)
  const [gate, setGate] = useState(null);        // array of missing-field labels (null = ok)
  const [copied, setCopied] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [marketOpen, setMarketOpen] = useState(false);
  const [outreachOpen, setOutreachOpen] = useState(false);
  const [negotiationOpen, setNegotiationOpen] = useState(false);
  const [docsRefresh, setDocsRefresh] = useState(0);   // bump after a contract is generated
  const [emailRefresh, setEmailRefresh] = useState(0); // bump after a send from outside the panel
  const emailRef = useRef(null);
  const docsRef = useRef(null);

  // silent: refresh in place without the "Loading deal…" screen. Modals call this on success — a
  // non-silent reload replaces the page and unmounts the modal before its result screen shows.
  const loadDeal = useCallback(({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    fetch(`/api/deals/${id}`).then(r => r.json())
      .then(d => { setDeal(d && !d.error ? d : null); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);
  useEffect(() => { loadDeal(); }, [loadDeal]);

  // Notification links land on #email — scroll the Email panel into view once the deal has rendered.
  useEffect(() => {
    if (!loading && deal && window.location.hash === '#email') {
      setTimeout(() => emailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [loading, deal]);

  // Comps for the pitch picker — same engine as underwriting / the sourcing map.
  useEffect(() => {
    if (!deal) return;
    const p = new URLSearchParams({
      deal_id: String(id),
      zip: deal.zip || '',
      sqft: String(deal.sqft || ''),
      year_built: String(deal.year_built || ''),
      subject_mls_id: deal.mls_id || '',
      lat: String(deal.lat || ''),
      lng: String(deal.lng || ''),
      address: deal.address || '',
    });
    fetch(`/api/mls-opportunities/comps?${p.toString()}`).then(r => r.json())
      .then(d => setComps(Array.isArray(d?.comps) ? d.comps : (Array.isArray(d) ? d : [])))
      .catch(() => setComps([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id]);

  // Optimistic inline edit → PUT /api/deals/:id (mirrors the list view's source-flag behavior).
  const patch = useCallback(async (fields) => {
    const local = { ...fields };
    if ('ai_arv' in fields) local.arv_source = 'manual';
    if ('max_offer' in fields) local.mao_source = 'manual';
    if ('repair_cost' in fields) local.repair_source = 'manual';
    setDeal(prev => ({ ...prev, ...local }));
    try {
      const r = await fetch(`/api/deals/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
    } catch (e) { alert(`Save failed: ${e.message}`); loadDeal(); }
  }, [id, loadDeal]);


  const toggleComp = (cid) => setSelected(prev => { const n = new Set(prev); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });

  const generatePitch = () => {
    const chosen = (comps || []).filter(c => selected.has(c.id));
    const missing = pitchMissing(deal, chosen.length);
    if (missing.length) { setGate(missing); return; }
    setGate(null);
    setPitch(buildPitch(deal, chosen));
  };
  const copyPitch = async () => {
    try { await navigator.clipboard.writeText(pitch || ''); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { alert('Copy failed — select the text and copy manually.'); }
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--gray-500)' }}>Loading deal…</div>;
  if (!deal) return <div style={{ padding: 40, color: 'var(--gray-500)' }}>Deal not found. <span onClick={() => navigate('/deals')} style={{ color: 'var(--teal)', cursor: 'pointer' }}>← Back to Deals</span></div>;

  const offerMade = deal.offer_amount != null && deal.offer_amount !== '';
  const photos = Array.isArray(deal.photos) ? deal.photos : (deal.photo_url ? [deal.photo_url] : []);
  const cover = photos[0] || deal.photo_url;

  return (
    <div style={{ maxWidth: 1220, margin: '0 auto' }}>
      {/* Top bar */}
      <div onClick={() => navigate('/deals')} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--gray-500)', cursor: 'pointer', marginBottom: 8 }}>← Back to Deals</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 25, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', margin: '0 0 6px' }}>{deal.address}</h1>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>{[deal.city, deal.state, deal.zip].filter(Boolean).join(', ')} · Deal #{deal.id}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            <StageBadge stage={deal.stage} dealId={deal.id} deal={deal} onChanged={loadDeal} />
            <ListingStatusCell deal={deal} onSave={v => patch({ listing_status: v })} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <button style={btn} onClick={() => docsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Documents</button>
          <button style={btn} onClick={() => setOutreachOpen(true)}>Email Agent</button>
          <button style={btn} onClick={() => setContractOpen(true)}>Generate Contract</button>
          {/* "Market to Buyers" removed while buyer blasting is disabled — see
              POST /api/deals/:id/blast-buyers (503). Re-add when the blast is rebuilt. */}
        </div>
      </div>

      {/* Snapshot */}
      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20, marginBottom: 20 }}>
        <div>
          {cover ? (
            <img src={proxyPhoto(cover)} alt="" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 14, border: '1px solid var(--navy-border)' }} />
          ) : (
            <div style={{ width: '100%', aspectRatio: '4/3', borderRadius: 14, border: '1px solid var(--navy-border)', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-500)', fontSize: 13 }}>No photo</div>
          )}
        </div>
        <div style={card}>
          <div style={{ ...lbl, marginBottom: 14 }}>Deal Snapshot</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '18px 14px' }}>
            <Metric label="List Price"><span style={{ fontSize: 19, fontWeight: 700, color: 'var(--gray-100)' }}>{fmtMoney(deal.asking_price)}</span></Metric>
            <Metric label="ARV"><InlineMoney value={deal.ai_arv} onSave={v => patch({ ai_arv: v })} source={deal.arv_source} verifiedColor="var(--white)" /></Metric>
            <Metric label="Rehab"><InlineMoney value={deal.repair_cost} onSave={v => patch({ repair_cost: v })} source={deal.repair_source} verifiedColor="var(--white)" /></Metric>
            <Metric label={offerMade ? 'Offer' : 'MAO'}>
              {offerMade
                ? <InlineMoney value={deal.offer_amount} onSave={v => patch({ offer_amount: v })} source="manual" verifiedColor="var(--teal)" tag="OFFER" />
                : <InlineMoney value={deal.max_offer} onSave={v => patch({ max_offer: v })} source={deal.mao_source} verifiedColor="var(--teal)" hint="Set MAO" />}
            </Metric>
            <Metric label="Assignment Fee"><span style={{ fontSize: 18, fontWeight: 700, color: '#b9821a' }}>{fmtMoney(deal.fee ?? deal.assignment_fee)}</span></Metric>
            <Metric label="AI Score"><span style={{ fontSize: 18, fontWeight: 700, color: 'var(--white)' }}>{deal.ai_deal_score ?? '—'}</span></Metric>
            <Metric label="Offer Date"><span style={{ fontSize: 14, color: 'var(--gray-100)' }}>{fmtDate(deal.offer_date)}</span></Metric>
            <Metric label="Follow-up"><FollowUpBadge next={deal.next_followup_at} last={deal.last_followup_at} /></Metric>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 372px', gap: 20, alignItems: 'start' }}>
        {/* Main */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Facts */}
          <div style={card}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              {[['Beds', deal.beds], ['Baths', deal.baths], ['Garage', deal.garage != null ? `${deal.garage} car` : '—'], ['Sqft', num(deal.sqft) || '—'],
                ['Lot', deal.lot_size || '—'], ['Year', deal.year_built || '—'], ['Pool', deal.pool ? 'Yes' : 'No'], ['Type', deal.property_type || deal.deal_type || '—']].map(([l, v]) => (
                <Metric key={l} label={l}><span style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)' }}>{v ?? '—'}</span></Metric>
              ))}
            </div>
            {deal.mls_id && <div style={{ marginTop: 14 }}><span onClick={() => navigate(`/underwriting/${deal.id}`)} style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--teal)', cursor: 'pointer' }}>Open full underwriting &amp; comps →</span></div>}
          </div>

          {/* Deal terms & marketing info */}
          <div style={card}>
            <div style={{ ...h2, marginBottom: 4 }}>Deal terms &amp; marketing info</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 16 }}>Feeds the pitch &amp; packet.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              <TermField label="Strategy" value={deal.strategy} onSave={v => patch({ strategy: v })} />
              <TermField label="Neighborhood / subdivision" value={deal.neighborhood} onSave={v => patch({ neighborhood: v })} />
              <TermField label="Garage (cars)" value={deal.garage} onSave={v => patch({ garage: v })} type="number" />
              <TermField label="Pool" value={deal.pool ? '1' : '0'} onSave={v => patch({ pool: Number(v) })} options={[{ v: '0', l: 'No' }, { v: '1', l: 'Yes' }]} />
              {/* The pitch's "Price:" line — what we are SELLING at, not what the seller listed at
                  (that stays on the List Price metric above). Migration 076 split the two. */}
              <TermField label="Sell price" value={deal.marketing_price ?? deal.asking_price} onSave={v => patch({ marketing_price: v })} type="number" />
              <TermField label="EMD" value={deal.earnest_money} onSave={v => patch({ earnest_money: v })} type="number" />
              {/* What happened to that money (migration 079). NOTHING sets this automatically — the
                  Dashboard prompts when the option period is ending or the deal sells or dies, and
                  the agent records the answer here. A status the app decided would read as a fact the
                  agent had confirmed. */}
              <TermField label="EMD status" value={deal.emd_status || ''} onSave={v => patch({ emd_status: v || null })}
                options={[{ v: '', l: '— not recorded' }, { v: 'sent', l: 'Sent' },
                          { v: 'pending', l: 'Pending' }, { v: 'returned', l: 'Returned' },
                          { v: 'lost', l: 'Lost' }, { v: 'none', l: 'None sent' }]} />
              {/* Required when the money is lost — the API and the database both refuse it otherwise. */}
              {deal.emd_status === 'lost' && (
                <TermField label="Why it was lost" value={deal.emd_note} onSave={v => patch({ emd_note: v })} full />
              )}
              <TermField label="Title company" value={deal.title_company} onSave={v => patch({ title_company: v })} />
              <TermField label="COE" value={deal.close_of_escrow} onSave={v => patch({ close_of_escrow: v })} />
              <TermField label="In an HOA?" value={deal.hoa == null ? '' : String(deal.hoa)} onSave={v => patch({ hoa: v === '' || v == null ? null : Number(v) })} options={[{ v: '', l: '— unknown' }, { v: '1', l: 'Yes' }, { v: '0', l: 'No' }]} />
              {deal.hoa !== 0 && <TermField label="HOA / month" value={deal.hoa_monthly} onSave={v => patch({ hoa_monthly: v })} type="number" />}
              {deal.hoa !== 0 && <TermField label="HOA prepaid" value={deal.hoa_prepaid} onSave={v => patch({ hoa_prepaid: v })} type="number" />}
              {deal.hoa !== 0 && <TermField label="HOA disclosure fee" value={deal.hoa_disclosure} onSave={v => patch({ hoa_disclosure: v })} type="number" />}
              <TermField label="Lot size (sq ft)" value={deal.lot_size} onSave={v => patch({ lot_size: v })} />
              <TermField label="Year built" value={deal.year_built} onSave={v => patch({ year_built: v })} type="number" />
              <TermField label="Access notes" value={deal.access_notes} onSave={v => patch({ access_notes: v })} full />
              <TermField label="📷 Photos link (Dropbox / Drive gallery)" value={deal.photos_link} onSave={v => patch({ photos_link: v })} full />
              <TermField label='Pitch details (the "Condition:" body of the pitch)' value={deal.pitch_details} onSave={v => patch({ pitch_details: v })} rows={4} full />
            </div>
          </div>

          {/* Pitch comps picker */}
          <div style={card}>
            <div style={{ ...h2, marginBottom: 4 }}>Comps for the pitch</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 14 }}>Pulled from the MLS underwrite. Check the ones to include in the pitch — at least one is required.</div>
            {comps == null ? <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Loading comps…</div>
              : comps.length === 0 ? <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>No MLS comps found for this property. <span onClick={() => navigate(`/underwriting/${deal.id}`)} style={{ color: 'var(--teal)', cursor: 'pointer' }}>Open underwriting →</span></div>
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {comps.map(c => {
                    const on = selected.has(c.id);
                    return (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 11px', border: `1px solid ${on ? 'var(--teal)' : 'var(--navy-border)'}`, borderRadius: 9, cursor: 'pointer', background: on ? 'rgba(20,80,74,0.06)' : 'transparent' }}>
                        <input type="checkbox" checked={on} onChange={() => toggleComp(c.id)} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{c.address || '(address n/a)'}</span>
                          <span style={{ fontSize: 11.5, color: 'var(--gray-500)', marginLeft: 8 }}>{[c.beds && `${c.beds}bd`, c.baths && `${c.baths}ba`, c.sqft && `${num(c.sqft)} sqft`].filter(Boolean).join(' · ')}</span>
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', whiteSpace: 'nowrap' }}>{fmtMoney(c.sold_price ?? c.price)}</span>
                        <span style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{c.close_date ? fmtDate(c.close_date) : ''}</span>
                      </label>
                    );
                  })}
                </div>
              )}
          </div>

          {/* Marketing */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 10 }}>
              <div style={h2}>Marketing</div>
              <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>Generate a text pitch or a full property packet</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {/* Pitch */}
              <div style={{ border: '1px solid var(--navy-border)', borderRadius: 12, padding: '15px 16px', background: 'var(--navy)', display: 'flex', flexDirection: 'column', gap: 11 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: 20 }}>✍️</span><div><div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)' }}>Pitch</div><div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>Text only — copy or send</div></div></div>
                <div style={{ fontSize: 12, color: 'var(--gray-300)', flex: 1 }}>Auto-filled from the deal in your exact format. Pool line only if there's a pool. Fully editable.</div>
                <button style={btnPrimary} onClick={generatePitch}>Generate pitch</button>
              </div>
              {/* Packet — Pass 2 */}
              <div style={{ border: '1px dashed var(--navy-border)', borderRadius: 12, padding: '15px 16px', background: 'var(--navy)', display: 'flex', flexDirection: 'column', gap: 11, opacity: 0.85 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: 20 }}>📄</span><div><div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)' }}>Property Packet</div><div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>Full report — PDF + web link</div></div></div>
                <div style={{ fontSize: 12, color: 'var(--gray-300)', flex: 1 }}>Cover + photos, tax record, ARV &amp; comps, rehab, terms.</div>
                <button style={{ ...btn, cursor: 'default', opacity: 0.6 }} disabled title="Next pass">Coming next pass</button>
              </div>
            </div>

            {/* Missing-info gate */}
            {gate && (
              <div style={{ marginTop: 14, border: '1px solid rgba(184,84,80,0.4)', background: 'rgba(184,84,80,0.07)', borderRadius: 11, padding: '14px 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#B85450', marginBottom: 8 }}>⚠︎ Add this before generating the pitch:</div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>{gate.map(m => <li key={m} style={{ fontSize: 12.5, color: 'var(--gray-100)', marginBottom: 3 }}>{m}</li>)}</ul>
                <div style={{ marginTop: 10 }}><button style={btn} onClick={() => setGate(null)}>Dismiss</button></div>
              </div>
            )}

            {/* Generated pitch — editable */}
            {pitch != null && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6 }}>Pitch — edit freely before sending</div>
                <textarea value={pitch} onChange={e => setPitch(e.target.value)} rows={20}
                  style={{ ...inputStyle, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12.5, lineHeight: 1.5, resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button style={btnPrimary} onClick={copyPitch}>{copied ? '✓ Copied' : 'Copy'}</button>
                  <button style={btn} onClick={generatePitch}>Regenerate from deal</button>
                  {/* "Send to buyer list" removed while buyer blasting is disabled. The pitch
                      text above is still generated and copyable. */}
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div style={card}>
            <div style={{ ...h2, marginBottom: 12 }}>Property description</div>
            <TermField label="" value={deal.description} onSave={v => patch({ description: v })} rows={4} full />
          </div>
        </div>

        {/* Rail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Documents — generated contracts, assignments, uploads. First in the rail so they're on
              screen when the deal opens (previously only reachable via /underwriting/:id). */}
          <div ref={docsRef} style={{ scrollMarginTop: 16 }}>
            <DealDocuments dealId={deal.id} compact refreshKey={docsRefresh}
              onStageChange={() => loadDeal({ silent: true })} />
          </div>

          {/* Email — offers and follow-ups from the user's own Gmail, with the agent's replies. */}
          <div ref={emailRef}>
            <DealEmailPanel deal={deal} refreshKey={emailRefresh} onChanged={() => loadDeal({ silent: true })} />
          </div>

          {/* Agent & offer */}
          <div style={card}>
            <div style={{ ...h2, marginBottom: 12 }}>Agent &amp; offer</div>
            <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--navy-border)', marginBottom: 12 }}>
              {deal.agent_name || deal.agent_phone || deal.agent_email ? (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)' }}>{deal.agent_name || '—'}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 2 }}>{[deal.agent_phone, deal.agent_email].filter(Boolean).join(' · ') || 'No contact on file'}</div>
                </>
              ) : <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>No agent on file.</div>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <TermField label="Offer amount" value={deal.offer_amount} onSave={v => patch({ offer_amount: v })} type="number" />
              <TermField label="Offer date" value={deal.offer_date ? String(deal.offer_date).slice(0, 10) : ''} onSave={v => patch({ offer_date: v })} type="date" />
              <div style={{ gridColumn: '1 / -1' }}>
                <TermField label="Offer status" value={deal.offer_status || ''} onSave={v => patch({ offer_status: v })}
                  options={[{ v: '', l: '—' }, { v: 'Awaiting response', l: 'Awaiting response' }, { v: 'Countered', l: 'Countered' }, { v: 'Accepted', l: 'Accepted' }, { v: 'Rejected', l: 'Rejected' }]} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button style={{ ...btnPrimary, flex: 1 }} onClick={() => setNegotiationOpen(true)}>Log reply</button>
              <button style={{ ...btn, flex: 1 }} onClick={() => setNegotiationOpen(true)}>Counter →</button>
            </div>
          </div>

          {/* Notes */}
          <div style={card}>
            <div style={{ ...h2, marginBottom: 12 }}>Notes</div>
            <NotesThread dealId={deal.id} />
          </div>
        </div>
      </div>

      {/* Modals (reused from the existing flows) */}
      {/* Success callbacks refresh silently and leave the modal open — each has its own result screen
          (contract download/send, sent confirmation) that the user closes themselves. */}
      {contractOpen && <ContractModal deal={deal} dealId={deal.id} onClose={() => setContractOpen(false)} onGenerated={() => { loadDeal({ silent: true }); setDocsRefresh(k => k + 1); }} />}
      {marketOpen && <MarketDealModal deal={deal} onClose={() => setMarketOpen(false)} />}
      {outreachOpen && <GmailComposeModal deal={deal} mode="new" onClose={() => setOutreachOpen(false)} onSent={() => { loadDeal({ silent: true }); setEmailRefresh(k => k + 1); }} />}
      {negotiationOpen && <NegotiationModal deal={deal} dealId={deal.id} onClose={() => setNegotiationOpen(false)} onSent={() => loadDeal({ silent: true })} />}
    </div>
  );
}
